import { Action, RequestStatus, UseCaseStateGenerator } from '@lib/plugin-redux-core';
import {
  FIND_GLOBAL_FORMS_USE_CASE,
  FindGlobalFormsUseCaseOutput,
  SEARCH_SPACE_OBJECTS_USE_CASE,
  SpaceObjects,
  GlobalForm,
  FormSummary as DomainFormSummary,
} from '@module/form';
import { FormEditPageState } from '../form-edit.states';
import { SelectFormFetchMode } from './select-form-modal.state';
import {
  GetGlobalFormsUseCaseState,
  GetLocalFormsUseCaseState,
  SearchGlobalFormsUseCaseState,
  SearchLocalFormsUseCaseState,
} from './select-form-modal.state';

type SelectFormUseCasePayload = {
  search?: string;
  selectFormContext?: boolean;
  nextToken?: string;
  formNextToken?: string;
};

const isSelectFormContext = (payload: unknown): boolean =>
  Boolean(
    payload &&
    typeof payload === 'object' &&
    (payload as SelectFormUseCasePayload).selectFormContext,
  );

const hasSearchQuery = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return false;
  const search = (payload as SelectFormUseCasePayload).search;
  return typeof search === 'string' && search.trim().length > 0;
};

const hasNextToken = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return false;
  const p = payload as SelectFormUseCasePayload;
  const token = p.nextToken ?? p.formNextToken;
  return typeof token === 'string' && token.length > 0;
};

const resolveFetchMode = (payload: unknown): SelectFormFetchMode => {
  if (hasNextToken(payload)) return 'loadMore';
  if (hasSearchQuery(payload)) return 'search';
  return 'load';
};

function mergeArrays<T extends { id: any }>(existing: T[], incoming: T[]): T[] {
  const seen = new Set(existing.map((f) => f.id.toString()));
  const appended = incoming.filter((f) => !seen.has(f.id.toString()));
  return [...existing, ...appended];
}

export const handleFindGlobalFormsForSelectFormModal: UseCaseStateGenerator<GetGlobalFormsUseCaseState> =
  {
    name: FIND_GLOBAL_FORMS_USE_CASE,
    executing: (state: FormEditPageState, action: Action): FormEditPageState => {
      if (!isSelectFormContext(action.payload)) {
        return state;
      }
      const mode = resolveFetchMode(action.payload);
      const isSearch = hasSearchQuery(action.payload);
      const isLoadMore = mode === 'loadMore';

      if (isLoadMore) {
        return {
          ...state,
          selectFormPendingGlobalMode: mode,
          selectFormPendingGlobalIsSearch: isSearch,
          ...(isSearch
            ? { searchGlobalFormsStatus: RequestStatus.EXECUTE }
            : { getGlobalFormsStatus: RequestStatus.EXECUTE }),
        };
      }

      return {
        ...state,
        selectFormPendingGlobalMode: mode,
        selectFormPendingGlobalIsSearch: isSearch,
        ...(isSearch
          ? { searchGlobalFormsStatus: RequestStatus.EXECUTE }
          : { getGlobalFormsStatus: RequestStatus.EXECUTE }),
      };
    },
    success: (state: FormEditPageState, action: Action): FormEditPageState => {
      if (state.selectFormPendingGlobalMode === null) {
        return state;
      }
      const payload = action.payload as FindGlobalFormsUseCaseOutput;
      const forms = payload?.globalForms ?? [];
      const nextToken = payload?.nextToken ?? null;
      const mode = state.selectFormPendingGlobalMode;
      const isSearch = state.selectFormPendingGlobalIsSearch;

      if (mode === 'loadMore') {
        if (isSearch) {
          return {
            ...state,
            globalFormsSearchResult: mergeArrays(state.globalFormsSearchResult, forms),
            globalFormsSearchNextToken: nextToken,
            searchGlobalFormsStatus: RequestStatus.SUCCESS,
            selectFormPendingGlobalMode: null,
            selectFormPendingGlobalIsSearch: false,
          };
        }
        return {
          ...state,
          globalForms: mergeArrays(state.globalForms, forms),
          globalFormsNextToken: nextToken,
          getGlobalFormsStatus: RequestStatus.SUCCESS,
          selectFormPendingGlobalMode: null,
          selectFormPendingGlobalIsSearch: false,
        };
      }

      if (isSearch) {
        return {
          ...state,
          globalFormsSearchResult: forms,
          globalFormsSearchNextToken: nextToken,
          searchGlobalFormsStatus: RequestStatus.SUCCESS,
          selectFormPendingGlobalMode: null,
          selectFormPendingGlobalIsSearch: false,
        };
      }

      return {
        ...state,
        globalForms: forms,
        globalFormsNextToken: nextToken,
        getGlobalFormsStatus: RequestStatus.SUCCESS,
        selectFormPendingGlobalMode: null,
        selectFormPendingGlobalIsSearch: false,
      };
    },
    error: (state: FormEditPageState, action: Action): FormEditPageState => {
      if (state.selectFormPendingGlobalMode === null) {
        return state;
      }
      const isSearch = state.selectFormPendingGlobalIsSearch;
      return {
        ...state,
        ...(isSearch
          ? { searchGlobalFormsStatus: RequestStatus.ERROR }
          : { getGlobalFormsStatus: RequestStatus.ERROR }),
        error: action.error,
        selectFormPendingGlobalMode: null,
        selectFormPendingGlobalIsSearch: false,
      };
    },
    reset: (state: FormEditPageState): FormEditPageState => ({
      ...state,
      getGlobalFormsStatus: RequestStatus.RESET,
      searchGlobalFormsStatus: RequestStatus.RESET,
      globalFormsSearchResult: [],
      globalFormsNextToken: null,
      globalFormsSearchNextToken: null,
      selectFormPendingGlobalMode: null,
      selectFormPendingGlobalIsSearch: false,
    }),
  };

export const handleSearchSpaceObjectsForSelectFormModal: UseCaseStateGenerator<
  GetLocalFormsUseCaseState & SearchLocalFormsUseCaseState
> = {
  name: SEARCH_SPACE_OBJECTS_USE_CASE,
  executing: (state: FormEditPageState, action: Action): FormEditPageState => {
    if (!isSelectFormContext(action.payload)) {
      return state;
    }
    const mode = resolveFetchMode(action.payload);
    const isSearch = hasSearchQuery(action.payload);
    const isLoadMore = mode === 'loadMore';

    if (isLoadMore) {
      return {
        ...state,
        selectFormPendingLocalMode: mode,
        selectFormPendingLocalIsSearch: isSearch,
        ...(isSearch
          ? { searchLocalFormsStatus: RequestStatus.EXECUTE }
          : { getLocalFormsStatus: RequestStatus.EXECUTE }),
      };
    }

    return {
      ...state,
      selectFormPendingLocalMode: mode,
      selectFormPendingLocalIsSearch: isSearch,
      ...(isSearch
        ? { searchLocalFormsStatus: RequestStatus.EXECUTE }
        : { getLocalFormsStatus: RequestStatus.EXECUTE }),
    };
  },
  success: (state: FormEditPageState, action: Action): FormEditPageState => {
    const payload = action.payload as SpaceObjects;
    const forms = payload?.forms ?? [];
    const formNextToken = payload?.formNextToken ?? null;
    const folderNextToken = payload?.folderNextToken ?? null;
    const mode = state.selectFormPendingLocalMode || 'load';
    const isSearch = state.selectFormPendingLocalIsSearch;

    if (mode === 'loadMore') {
      if (isSearch) {
        return {
          ...state,
          localFormsSearchResult: mergeArrays(state.localFormsSearchResult, forms),
          localFormsSearchNextToken: formNextToken,
          localFolderSearchNextToken: folderNextToken,
          searchLocalFormsStatus: RequestStatus.SUCCESS,
          selectFormPendingLocalMode: null,
          selectFormPendingLocalIsSearch: false,
        };
      }
      return {
        ...state,
        localForms: mergeArrays(state.localForms, forms),
        localFormsNextToken: formNextToken,
        localFolderNextToken: folderNextToken,
        getLocalFormsStatus: RequestStatus.SUCCESS,
        selectFormPendingLocalMode: null,
        selectFormPendingLocalIsSearch: false,
      };
    }

    if (isSearch) {
      return {
        ...state,
        localFormsSearchResult: forms,
        localFormsSearchNextToken: formNextToken,
        localFolderSearchNextToken: folderNextToken,
        searchLocalFormsStatus: RequestStatus.SUCCESS,
        selectFormPendingLocalMode: null,
        selectFormPendingLocalIsSearch: false,
      };
    }

    return {
      ...state,
      localForms: forms,
      localFormsNextToken: formNextToken,
      localFolderNextToken: folderNextToken,
      getLocalFormsStatus: RequestStatus.SUCCESS,
      selectFormPendingLocalMode: null,
      selectFormPendingLocalIsSearch: false,
    };
  },
  error: (state: FormEditPageState, action: Action): FormEditPageState => {
    if (state.selectFormPendingLocalMode === null) {
      return state;
    }
    const isSearch = state.selectFormPendingLocalIsSearch;
    return {
      ...state,
      ...(isSearch
        ? { searchLocalFormsStatus: RequestStatus.ERROR }
        : { getLocalFormsStatus: RequestStatus.ERROR }),
      error: action.error,
      selectFormPendingLocalMode: null,
      selectFormPendingLocalIsSearch: false,
    };
  },
  reset: (state: FormEditPageState): FormEditPageState => ({
    ...state,
    getLocalFormsStatus: RequestStatus.RESET,
    searchLocalFormsStatus: RequestStatus.RESET,
    localFormsSearchResult: [],
    localFormsNextToken: null,
    localFolderNextToken: null,
    localFormsSearchNextToken: null,
    localFolderSearchNextToken: null,
    selectFormPendingLocalMode: null,
    selectFormPendingLocalIsSearch: false,
  }),
};

export const selectFormModalHandlers = [
  handleFindGlobalFormsForSelectFormModal,
  handleSearchSpaceObjectsForSelectFormModal,
];
